import { Alert, Button, Card, Form, Input, Select, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type TitleRow = {
  id: string;
  code: string;
  title: string;
  author?: string | null;
  isbn?: string | null;
  publisher?: string | null;
  is_active?: number | boolean;
};

type FormValues = {
  code: string;
  title: string;
  author?: string;
  isbn?: string;
  publisher?: string;
  is_active?: boolean;
};

export default function LibraryTitleFormPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.library.manage') || user?.permissions?.includes('education.admin')
  );
  const isNew = !id || id === 'new';
  const [form] = Form.useForm<FormValues>();

  const existing = useQuery({
    queryKey: ['education-library-title', id],
    enabled: !isNew && Boolean(id),
    queryFn: async () => {
      const res = await api.get<TitleRow>(`/education/library/titles/${id}`);
      return res.data;
    },
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ is_active: true });
      return;
    }
    const row = existing.data;
    if (!row) return;
    form.setFieldsValue({
      code: row.code,
      title: row.title,
      author: row.author || undefined,
      isbn: row.isbn || undefined,
      publisher: row.publisher || undefined,
      is_active: !(row.is_active === 0 || row.is_active === false),
    });
  }, [existing.data, form, isNew]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      if (isNew) {
        return api.post<{ id: string }>('/education/library/titles', values);
      }
      return api.put(`/education/library/titles/${id}`, values);
    },
    onSuccess: (res) => {
      message.success(isNew ? t('libraryTitleCreated') : t('libraryTitleUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-library-titles'] });
      queryClient.invalidateQueries({ queryKey: ['education-library-title'] });
      const nextId = isNew ? (res.data as { id?: string })?.id : id;
      if (nextId) navigate(`/education/library/titles/${nextId}/copies`);
      else navigate('/education/library/titles');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={isNew ? t('newLibraryTitle') : t('editLibraryTitle')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  if (!isNew && existing.isError) {
    return (
      <EducationPageChrome title={t('editLibraryTitle')}>
        <Alert type="error" showIcon message={t('unavailable', { status: 404 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={isNew ? t('newLibraryTitle') : t('editLibraryTitle')}
      description={t('libraryTitlesIntro')}
      extra={
        <Space>
          {!isNew && id ? (
            <Button onClick={() => navigate(`/education/library/titles/${id}/copies`)}>{t('viewCopies')}</Button>
          ) : null}
          <Button onClick={() => navigate('/education/library/titles')}>{t('backToTitles')}</Button>
        </Space>
      }
    >
      <Card size="small" style={{ maxWidth: 640 }} loading={!isNew && existing.isLoading}>
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title" label={t('title')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="author" label={t('author')}>
            <Input />
          </Form.Item>
          <Form.Item name="isbn" label={t('isbn')}>
            <Input />
          </Form.Item>
          <Form.Item name="publisher" label={t('publisher')}>
            <Input />
          </Form.Item>
          {!isNew ? (
            <Form.Item name="is_active" label={t('status')} rules={[{ required: true }]}>
              <Select
                options={[
                  { value: true, label: t('active') },
                  { value: false, label: t('inactive') },
                ]}
              />
            </Form.Item>
          ) : null}
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              {t('save')}
            </Button>
            <Button onClick={() => navigate('/education/library/titles')}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
